import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BookOpen,
  RefreshCw,
  Save,
  Download,
  Upload,
  Eye,
  Edit3,
  History,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Clock,
  Sparkles,
  FileText,
  Copy,
  ExternalLink,
} from "lucide-react";
import {
  fetchKnowledgeBase,
  generateKnowledgeBase,
  updateKnowledgeBase,
  fetchKnowledgeHistory,
  syncKnowledgeToS3,
  fetchClaw9Accounts,
  fetchBotAgents,
} from "../api/claw9";

// Note: Knowledge sections are now parsed dynamically from content
// to support both English and Vietnamese headers

/**
 * Claw9KnowledgeBase Component
 *
 * Displays and manages the AI-generated knowledge base derived from FAQs.
 * Supports viewing, editing, version history, and S3 synchronization.
 */
const Claw9KnowledgeBase = ({ onClose }) => {
  // ==================== STATE ====================
  const [accountId, setAccountId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState("preview"); // 'preview' | 'edit' | 'history'
  const [history, setHistory] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [s3Url, setS3Url] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);

  const editorRef = useRef(null);

  // ==================== LOAD KNOWLEDGE ====================
  const loadKnowledge = useCallback(async () => {
    if (!accountId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchKnowledgeBase(accountId, agentId);

      if (result.success && result.knowledge) {
        setKnowledge(result.knowledge);
        setEditedContent(result.knowledge.content || "");
        setS3Url(result.knowledge.s3_url || null);

        // Reset expanded sections (will be managed dynamically)
        setExpandedSections({});
      } else {
        // No knowledge exists yet
        setKnowledge(null);
        setEditedContent("");
      }
    } catch (err) {
      console.error("[KnowledgeBase] Failed to load:", err);
      setError(err.message || "Failed to load knowledge base");
    } finally {
      setIsLoading(false);
    }
  }, [accountId, agentId]);

  // ==================== LOAD HISTORY ====================
  const loadHistory = useCallback(async () => {
    if (!accountId) return;

    try {
      const result = await fetchKnowledgeHistory(accountId, agentId);
      if (result.success && result.history) {
        setHistory(result.history);
      }
    } catch (err) {
      console.error("[KnowledgeBase] Failed to load history:", err);
    }
  }, [accountId, agentId]);

  // ==================== EFFECTS ====================

  // Initialize: fetch account and agent IDs
  useEffect(() => {
    const initialize = async () => {
      setIsInitializing(true);
      setError(null);

      try {
        const [accountsResult, agentsResult] = await Promise.all([
          fetchClaw9Accounts(),
          fetchBotAgents(),
        ]);

        const accounts = accountsResult.accounts || [];
        const agents = agentsResult.agents || [];

        if (accounts.length > 0) {
          setAccountId(accounts[0].account_id);
        } else {
          setError('No accounts found. Please configure your account first.');
          setIsInitializing(false);
          setIsLoading(false);
          return;
        }

        if (agents.length > 0) {
          const defaultAgent = agents.find((agent) => agent.is_default) || agents[0];
          setAgentId(defaultAgent.name);
        }
      } catch (err) {
        console.error("[KnowledgeBase] Failed to initialize:", err);
        setError(err.message || "Failed to initialize. Please try again.");
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, []);

  // Load knowledge after account/agent are set
  useEffect(() => {
    if (accountId && !isInitializing) {
      loadKnowledge();
    }
  }, [accountId, isInitializing, loadKnowledge]);

  useEffect(() => {
    if (viewMode === "history" && accountId) {
      loadHistory();
    }
  }, [viewMode, accountId, loadHistory]);

  // ==================== GENERATE KNOWLEDGE ====================
  const handleGenerate = async (isRegenerate = false) => {
    if (!accountId) return;

    setIsGenerating(true);
    setError(null);
    setGenerationProgress("Fetching FAQs...");

    try {
      // Show progress updates
      const progressSteps = [
        "Fetching FAQs...",
        "Analyzing content structure...",
        "Generating product overview...",
        "Creating pricing section...",
        "Building industry responses...",
        "Crafting objection handling...",
        "Designing sales flow...",
        "Adding intent detection...",
        "Creating few-shot examples...",
        "Finalizing knowledge base...",
      ];

      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        stepIndex = Math.min(stepIndex + 1, progressSteps.length - 1);
        setGenerationProgress(progressSteps[stepIndex]);
      }, 3000);

      const result = await generateKnowledgeBase(accountId, agentId, {
        regenerate: isRegenerate,
        include_diary: !isRegenerate, // Include diary section for updates
      });

      clearInterval(progressInterval);

      if (result.success) {
        setKnowledge(result.knowledge);
        setEditedContent(result.knowledge.content || "");
        setSuccess(isRegenerate
          ? "Knowledge base regenerated successfully!"
          : "Knowledge base generated successfully!");

        // Auto-clear success message
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error("[KnowledgeBase] Failed to generate:", err);
      setError(err.message || "Failed to generate knowledge base");
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  // ==================== SAVE KNOWLEDGE ====================
  const handleSave = async () => {
    if (!accountId || !editedContent) return;

    setIsSaving(true);
    setError(null);

    try {
      const result = await updateKnowledgeBase(accountId, agentId, {
        content: editedContent,
        updated_by: "user_manual_edit",
      });

      if (result.success) {
        setKnowledge(result.knowledge);
        setSuccess("Knowledge base saved successfully!");
        setViewMode("preview");

        // Auto-clear success message
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error("[KnowledgeBase] Failed to save:", err);
      setError(err.message || "Failed to save knowledge base");
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== SYNC TO S3 ====================
  const handleSyncToS3 = async () => {
    if (!accountId) return;

    setIsSyncing(true);
    setError(null);

    try {
      const result = await syncKnowledgeToS3(accountId, agentId);

      if (result.success) {
        setS3Url(result.s3_url);
        setSuccess("Knowledge synced to S3 successfully!");

        // Auto-clear success message
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error("[KnowledgeBase] Failed to sync to S3:", err);
      setError(err.message || "Failed to sync to S3");
    } finally {
      setIsSyncing(false);
    }
  };

  // ==================== COPY S3 URL ====================
  const handleCopyS3Url = async () => {
    if (!s3Url) return;

    try {
      await navigator.clipboard.writeText(s3Url);
      setSuccess("S3 URL copied to clipboard!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("[KnowledgeBase] Failed to copy:", err);
    }
  };

  // ==================== RESTORE VERSION ====================
  const handleRestoreVersion = async (version) => {
    if (!version) return;

    setEditedContent(version.content);
    setViewMode("edit");
    setSuccess(`Restored version from ${formatDate(version.created_at)}. Review and save to apply.`);
  };

  // ==================== TOGGLE SECTION ====================
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // ==================== HELPERS ====================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getWordCount = (content) => {
    if (!content) return 0;
    return content.split(/\s+/).filter(Boolean).length;
  };

  // Parse all sections dynamically from content (supports any language headers)
  const parseSectionsFromContent = () => {
    if (!knowledge?.content) return [];

    const content = knowledge.content;
    const sections = [];

    // Match all ## headers
    const headerRegex = /^##\s+(.+)$/gm;
    let match;
    let lastIndex = 0;
    let lastHeader = null;

    while ((match = headerRegex.exec(content)) !== null) {
      if (lastHeader !== null) {
        const sectionContent = content.slice(lastIndex, match.index).trim();
        if (sectionContent) {
          sections.push({
            id: lastHeader.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            label: lastHeader,
            content: sectionContent,
          });
        }
      }
      lastHeader = match[1].trim();
      lastIndex = match.index + match[0].length;
    }

    // Add last section
    if (lastHeader !== null) {
      const sectionContent = content.slice(lastIndex).trim();
      if (sectionContent) {
        sections.push({
          id: lastHeader.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
          label: lastHeader,
          content: sectionContent,
        });
      }
    }

    return sections;
  };

  // ==================== RENDER: LOADING ====================
  if (isInitializing || isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[#0d1424] rounded-xl p-8">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
        <p className="mt-4 text-white/50">
          {isInitializing ? "Initializing..." : "Loading knowledge base..."}
        </p>
      </div>
    );
  }

  // ==================== RENDER: MAIN ====================
  return (
    <div className="h-full flex flex-col bg-[#0d1424] rounded-xl overflow-hidden">
      {/* ========== HEADER ========== */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg">
            <BookOpen className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">AI Knowledge Base</h2>
            <p className="text-xs text-white/50">
              {knowledge
                ? `Last updated: ${formatDate(knowledge.updated_at)}`
                : "No knowledge base generated yet"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Tabs */}
          <div className="flex bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode("preview")}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === "preview"
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <Eye className="w-4 h-4 inline mr-1" />
              Preview
            </button>
            <button
              onClick={() => setViewMode("edit")}
              disabled={!knowledge}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-50 ${
                viewMode === "edit"
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <Edit3 className="w-4 h-4 inline mr-1" />
              Edit
            </button>
            <button
              onClick={() => setViewMode("history")}
              disabled={!knowledge}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-50 ${
                viewMode === "history"
                  ? "bg-white/10 text-white"
                  : "text-white/50 hover:text-white"
              }`}
            >
              <History className="w-4 h-4 inline mr-1" />
              History
            </button>
          </div>

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* ========== ALERTS ========== */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-4 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          <span>{success}</span>
        </div>
      )}

      {/* ========== GENERATION PROGRESS ========== */}
      {isGenerating && generationProgress && (
        <div className="mx-4 mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Sparkles className="w-5 h-5 text-purple-400 animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-purple-300 font-medium">Generating with Claude Opus...</p>
              <p className="text-xs text-purple-400/70 mt-1">{generationProgress}</p>
            </div>
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
          </div>
          <div className="mt-3 h-1 bg-purple-500/20 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse"
                 style={{ width: "60%" }} />
          </div>
        </div>
      )}

      {/* ========== CONTENT AREA ========== */}
      <div className="flex-1 overflow-auto p-4">
        {/* No Knowledge State */}
        {!knowledge && !isGenerating && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="p-4 bg-purple-500/10 rounded-full mb-4">
              <Sparkles className="w-12 h-12 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Generate Your AI Knowledge Base
            </h3>
            <p className="text-white/50 max-w-md mb-6">
              Transform your FAQs into a structured knowledge base that helps AI
              understand your business and respond to customers more effectively.
            </p>
            <button
              onClick={() => handleGenerate(false)}
              disabled={isGenerating}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all"
            >
              <Sparkles className="w-5 h-5" />
              Generate Knowledge Base
            </button>
          </div>
        )}

        {/* Preview Mode */}
        {knowledge && viewMode === "preview" && (
          <div className="space-y-4">
            {/* Stats Bar */}
            <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-white/70">
                <FileText className="w-4 h-4" />
                <span>{getWordCount(knowledge.content).toLocaleString()} words</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Clock className="w-4 h-4" />
                <span>Version {knowledge.version || 1}</span>
              </div>
              {knowledge.generated_by && (
                <div className="flex items-center gap-2 text-sm text-purple-400">
                  <Sparkles className="w-4 h-4" />
                  <span>Generated by {knowledge.generated_by}</span>
                </div>
              )}
            </div>

            {/* S3 URL */}
            {s3Url && (
              <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300">Synced to S3:</span>
                <code className="flex-1 text-xs text-emerald-400/70 truncate">{s3Url}</code>
                <button
                  onClick={handleCopyS3Url}
                  className="p-1.5 hover:bg-emerald-500/20 rounded transition-colors"
                  title="Copy URL"
                >
                  <Copy className="w-4 h-4 text-emerald-400" />
                </button>
                <a
                  href={s3Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 hover:bg-emerald-500/20 rounded transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4 text-emerald-400" />
                </a>
              </div>
            )}

            {/* Sections Accordion - Dynamically parsed from content */}
            <div className="space-y-2">
              {(() => {
                // Use dynamic parsing to extract actual sections from content
                const dynamicSections = parseSectionsFromContent();

                // If dynamic parsing found sections, display them
                if (dynamicSections.length > 0) {
                  return dynamicSections.map((section) => {
                    const isExpanded = expandedSections[section.id];
                    // Check if section looks like an AI-related section
                    const isAISection = /intent|few-shot|fallback|reasoning|ai|mẫu|dự phòng|suy luận/i.test(section.label);

                    return (
                      <div
                        key={section.id}
                        className="border border-white/10 bg-white/5 rounded-lg overflow-hidden transition-colors"
                      >
                        <button
                          onClick={() => toggleSection(section.id)}
                          className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-white/5"
                        >
                          <div className="flex items-center gap-3">
                            {isAISection ? (
                              <Sparkles className="w-4 h-4 text-purple-400" />
                            ) : (
                              <FileText className="w-4 h-4 text-white/50" />
                            )}
                            <span className="text-sm font-medium text-white">
                              {section.label}
                            </span>
                          </div>
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-white/50" />
                            : <ChevronRight className="w-4 h-4 text-white/50" />
                          }
                        </button>

                        {isExpanded && (
                          <div className="p-4 border-t border-white/10 bg-[#0a0f1c]">
                            <pre className="text-sm text-white/70 whitespace-pre-wrap font-mono leading-relaxed">
                              {section.content}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  });
                }

                // Fallback: If no sections found, show raw content
                return (
                  <div className="border border-white/10 bg-white/5 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection("raw-content")}
                      className="w-full flex items-center justify-between p-3 text-left transition-colors hover:bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-white/50" />
                        <span className="text-sm font-medium text-white">
                          Full Knowledge Base Content
                        </span>
                      </div>
                      {expandedSections["raw-content"]
                        ? <ChevronDown className="w-4 h-4 text-white/50" />
                        : <ChevronRight className="w-4 h-4 text-white/50" />
                      }
                    </button>

                    {expandedSections["raw-content"] && (
                      <div className="p-4 border-t border-white/10 bg-[#0a0f1c]">
                        <pre className="text-sm text-white/70 whitespace-pre-wrap font-mono leading-relaxed">
                          {knowledge.content}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Edit Mode */}
        {knowledge && viewMode === "edit" && (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-white/50">
                Edit the markdown content below. Changes will be synced after saving.
              </p>
              <span className="text-xs text-white/30">
                {getWordCount(editedContent).toLocaleString()} words
              </span>
            </div>
            <textarea
              ref={editorRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="flex-1 w-full p-4 bg-[#0a0f1c] border border-white/10 rounded-lg text-white/80 font-mono text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              placeholder="Knowledge base content..."
              spellCheck={false}
            />
          </div>
        )}

        {/* History Mode */}
        {knowledge && viewMode === "history" && (
          <div className="space-y-3">
            <p className="text-sm text-white/50 mb-4">
              View and restore previous versions of your knowledge base.
            </p>

            {history.length === 0 ? (
              <div className="text-center py-8 text-white/30">
                No version history available
              </div>
            ) : (
              history.map((version, index) => (
                <div
                  key={version.id || index}
                  className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                    selectedVersion?.id === version.id
                      ? "border-purple-500/50 bg-purple-500/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  onClick={() => setSelectedVersion(version)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">
                        Version {version.version || history.length - index}
                      </p>
                      <p className="text-xs text-white/50 mt-1">
                        {formatDate(version.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {version.generated_by && (
                        <span className="text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                          {version.generated_by}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRestoreVersion(version);
                        }}
                        className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                  {version.change_summary && (
                    <p className="text-xs text-white/40 mt-2">
                      {version.change_summary}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ========== FOOTER ACTIONS ========== */}
      <div className="flex items-center justify-between p-4 border-t border-white/10 bg-[#0a0f1c]">
        <div className="flex items-center gap-2">
          {/* Generate/Regenerate Button */}
          <button
            onClick={() => handleGenerate(!!knowledge)}
            disabled={isGenerating || isSaving}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-purple-600/50 disabled:to-pink-600/50 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                {knowledge ? "Regenerate" : "Generate"}
              </>
            )}
          </button>

          {/* Sync to S3 Button */}
          {knowledge && (
            <button
              onClick={handleSyncToS3}
              disabled={isSyncing || isGenerating}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:cursor-not-allowed"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Sync to S3
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Save Button (Edit Mode) */}
          {viewMode === "edit" && (
            <>
              <button
                onClick={() => {
                  setEditedContent(knowledge?.content || "");
                  setViewMode("preview");
                }}
                className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || editedContent === knowledge?.content}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          )}

          {/* Download Button */}
          {knowledge && viewMode === "preview" && (
            <button
              onClick={() => {
                const blob = new Blob([knowledge.content], { type: "text/markdown" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `knowledge-base-${accountId}.md`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Claw9KnowledgeBase;
